// CreateUserManually.jsx - FIXED: All issues resolved
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    FormControlLabel,
    Switch,
    Chip,
    Grid,
    InputAdornment,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    Visibility,
    VisibilityOff,
    Email as EmailIcon,
    Person as PersonIcon,
    Lock as LockIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useNotification } from '../common/GlobalNotifications';
import PhoneInput from '../common/PhoneInput';
import TimezoneSelect from '../common/TimezoneSelect';

const CreateUserManually = ({ open, onClose, onUserCreated }) => {
    const { showSuccess, showError } = useNotification();
    const { t } = useTranslation();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [createdUser, setCreatedUser] = useState(null);
    const [step, setStep] = useState(t('hardcoded.components_auth_createusermanually.create'));
    const [userExists, setUserExists] = useState(false);
    const [existingUserInfo, setExistingUserInfo] = useState(null);
    const [checkingEmail, setCheckingEmail] = useState(false);

    // Validation schema that adapts based on user existence
    const getValidationSchema = () => {
        const baseSchema = {
            email: Yup.string()
                .email(t('createUser.validation.emailInvalid'))
                .required(t('createUser.validation.emailRequired')),
            name: Yup.string()
                .min(2, t('createUser.validation.nameMinLength'))
                .required(t('createUser.validation.nameRequired')),
            last_name: Yup.string()
                .min(2, t('createUser.validation.lastNameMinLength'))
                .required(t('createUser.validation.lastNameRequired')),
            language: Yup.string()
                .oneOf(['es', 'en'], t('createUser.validation.languageInvalid'))
                .required(t('createUser.validation.languageRequired')),
            type: Yup.string()
                .oneOf(['user', 'manager', 'admin'], t('createUser.validation.roleInvalid'))
                .required(t('createUser.validation.roleRequired')),
            timezone: Yup.string()
                .required(t('hardcoded.components_auth_createusermanually.zona_horaria_es_requerida'))
        };

        // Only require password fields if user doesn't exist
        if (!userExists) {
            baseSchema.password = Yup.string()
                .min(8, t('createUser.validation.passwordMinLength'))
                .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, t('createUser.validation.passwordPattern'))
                .required(t('createUser.validation.passwordRequired'));

            baseSchema.confirmPassword = Yup.string()
                .oneOf([Yup.ref(t('hardcoded.components_auth_createusermanually.password')), null], t('createUser.validation.passwordsMustMatch'))
                .required(t('createUser.validation.confirmPasswordRequired'));
        }

        return Yup.object(baseSchema);
    };

    const roleOptions = [
        {
            value: 'user',
            label: t('inviteUser.roles.user.label'),
            description: t('inviteUser.roles.user.description'),
            color: 'default'
        },
        {
            value: 'manager',
            label: t('inviteUser.roles.manager.label'),
            description: t('inviteUser.roles.manager.description'),
            color: 'warning'
        },
        {
            value: 'admin',
            label: t('inviteUser.roles.admin.label'),
            description: t('inviteUser.roles.admin.description'),
            color: 'error'
        }
    ];

    const languageOptions = [
        { value: 'es', label: t('createUser.languages.es') },
        { value: 'en', label: t('createUser.languages.en') }
    ];

    // Check if email exists
    const checkEmailExists = async (email) => {
        if (!email || !email.includes('@')) return;

        setCheckingEmail(true);
        try {
            const response = await api.auth.validateEmail({ email });
            if (response.data && response.data.exists) {
                setUserExists(true);
                setExistingUserInfo(response.data.user);
                showError(t('createUser.warnings.userExists'));
            } else {
                setUserExists(false);
                setExistingUserInfo(null);
            }
        } catch (error) {
            // If endpoint doesn't exist or errors, assume user doesn't exist
            setUserExists(false);
            setExistingUserInfo(null);
        } finally {
            setCheckingEmail(false);
        }
    };

    const { mutate: createUser, loading } = useMutation(
        api.auth.createUserManually,
        {
            onSuccess: (response) => {
                console.log(t('hardcoded.components_auth_createusermanually.user_created_successfully'), response);
                const data = response.data || response;

                setCreatedUser(data);
                setStep('success');
                showSuccess(t('createUser.notifications.userCreated'));

                if (onUserCreated) {
                    onUserCreated(data);
                }
            },
            onError: (error) => {
                console.error(t('hardcoded.components_auth_createusermanually.error_creating_user'), error);
                const errorMessage = error.response?.data?.detail ||
                    error.message ||
                    t('createUser.notifications.creationError');
                showError(errorMessage);
            }
        }
    );

    const formik = useFormik({
        initialValues: {
            email: '',
            name: '',
            last_name: '',
            phone: '',
            language: 'es',
            timezone: '',
            type: 'user',
            password: '',
            confirmPassword: '',
            send_welcome_email: true
        },
        validationSchema: getValidationSchema(),
        enableReinitialize: true,
        onSubmit: (values) => {
            console.log('Submitting form with values:', values);

            const userData = {
                email: values.email,
                name: values.name,
                last_name: values.last_name,
                phone: values.phone || null,
                language: values.language,
                timezone: values.timezone,
                type: values.type,
                send_welcome_email: values.send_welcome_email
            };

            // Only include password if user doesn't exist
            if (!userExists) {
                userData.password = values.password;
            }

            console.log('Creating user with data:', userData);
            createUser(userData);
        },
    });

    const handleClose = () => {
        setStep(t('hardcoded.components_auth_createusermanually.create'));
        setCreatedUser(null);
        setUserExists(false);
        setExistingUserInfo(null);
        formik.resetForm();
        onClose();
    };

    const generateRandomPassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let password = '';

        password += 'A';
        password += 'a';
        password += '1';

        for (let i = 0; i < 5; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        password = password.split('').sort(() => Math.random() - 0.5).join('');

        formik.setFieldValue(t('hardcoded.components_auth_createusermanually.password'), password);
        formik.setFieldValue('confirmPassword', password);
        showSuccess(t('createUser.notifications.passwordGenerated'));
    };

    const selectedRole = roleOptions.find(role => role.value === formik.values.type);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PersonAddIcon color="primary" />
                    <Typography variant="h6" component="div">
                        {step === 'create' ? t('createUser.title') : t('createUser.successTitle')}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {step === 'create' ? (
                    <Box>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            {t('createUser.subtitle')}
                        </Typography>

                        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 3 }}>
                            <Grid container spacing={3}>
                                {/* Personal Information */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        📋 {t('createUser.sections.personalInfo')}
                                    </Typography>
                                </Grid>

                                {/* Email with existence check */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        name="email"
                                        label={t('createUser.form.email')}
                                        type="email"
                                        value={formik.values.email}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            // Debounce email check
                                            clearTimeout(window.emailCheckTimeout);
                                            window.emailCheckTimeout = setTimeout(() => {
                                                checkEmailExists(e.target.value);
                                            }, 500);
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        helperText={formik.touched.email && formik.errors.email}
                                        InputProps={{
                                            startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                                            endAdornment: checkingEmail && (
                                                <InputAdornment position="end">
                                                    <CircularProgress size={20} />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    {userExists && existingUserInfo && (
                                        <Alert severity="info" sx={{ mt: 1 }}>
                                            <Typography variant="body2">
                                                <strong>{t('hardcoded.components_auth_createusermanually.usuario_existente')}</strong> {existingUserInfo.full_name}
                                                <br />
                                                {t('hardcoded.components_auth_createusermanually.este_usuario_se_agregara_a')}</Typography>
                                        </Alert>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="name"
                                        label={t('createUser.form.firstName')}
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.name && Boolean(formik.errors.name)}
                                        helperText={formik.touched.name && formik.errors.name}
                                        InputProps={{
                                            startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="last_name"
                                        label={t('createUser.form.lastName')}
                                        value={formik.values.last_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                                        helperText={formik.touched.last_name && formik.errors.last_name}
                                        InputProps={{
                                            startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <PhoneInput
                                        value={formik.values.phone}
                                        onChange={(value) => formik.setFieldValue('phone', value)}
                                        error={formik.touched.phone && Boolean(formik.errors.phone)}
                                        helperText={formik.touched.phone && formik.errors.phone}
                                    />
                                </Grid>

                                {/* Account Configuration */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                                        ⚙️ {t('createUser.sections.accountConfig')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>{t('createUser.form.language')}</InputLabel>
                                        <Select
                                            name="language"
                                            value={formik.values.language}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            label={t('createUser.form.language')}
                                        >
                                            {languageOptions.map((lang) => (
                                                <MenuItem key={lang.value} value={lang.value}>
                                                    {lang.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TimezoneSelect
                                        value={formik.values.timezone}
                                        onChange={(value) => formik.setFieldValue('timezone', value)}
                                        error={formik.touched.timezone && Boolean(formik.errors.timezone)}
                                        helperText={formik.touched.timezone && formik.errors.timezone}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl
                                        fullWidth
                                        error={formik.touched.type && Boolean(formik.errors.type)}
                                    >
                                        <InputLabel>{t('createUser.form.role')}</InputLabel>
                                        <Select
                                            name="type"
                                            value={formik.values.type}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            label={t('createUser.form.role')}
                                        >
                                            {roleOptions.map((role) => (
                                                <MenuItem key={role.value} value={role.value}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                        <Chip
                                                            label={role.label}
                                                            color={role.color}
                                                            size="small"
                                                            sx={{ minWidth: 80 }}
                                                        />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {role.description}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {formik.touched.type && formik.errors.type
                                                ? formik.errors.type
                                                : selectedRole?.description || t('inviteUser.form.roleHelper')
                                            }
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Password Configuration - Only if user doesn't exist */}
                                {!userExists && (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                                                🔐 {t('createUser.sections.password')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={generateRandomPassword}
                                                >
                                                    {t('createUser.form.generatePassword')}
                                                </Button>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('createUser.form.generatePasswordHelper')}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                name={t('hardcoded.components_auth_createusermanually.password')}
                                                label={t('createUser.form.password')}
                                                type={showPassword ? 'text' : 'password'}
                                                value={formik.values.password}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.password && Boolean(formik.errors.password)}
                                                helperText={formik.touched.password && formik.errors.password}
                                                InputProps={{
                                                    startAdornment: <LockIcon sx={{ mr: 1, color: 'action.active' }} />,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                edge="end"
                                                            >
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                name="confirmPassword"
                                                label={t('createUser.form.confirmPassword')}
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={formik.values.confirmPassword}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                                                InputProps={{
                                                    startAdornment: <LockIcon sx={{ mr: 1, color: 'action.active' }} />,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                                edge="end"
                                                            >
                                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* Email Settings */}
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="send_welcome_email"
                                                checked={formik.values.send_welcome_email}
                                                onChange={formik.handleChange}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2">
                                                    {t('createUser.form.sendWelcomeEmail')}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('createUser.form.sendWelcomeHelper')}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Alert severity="info" sx={{ mt: 3 }}>
                                <Typography variant="body2">
                                    <strong>{t('createUser.whatHappens.title')}</strong>
                                    <br />• {t('createUser.whatHappens.roleAssigned')} <strong>{selectedRole?.label}</strong>
                                    <br />• {formik.values.send_welcome_email ? t('createUser.whatHappens.emailCredentials') : t('createUser.whatHappens.manualCredentials')}
                                    {!userExists && (
                                        <>
                                            <br />• {t('createUser.whatHappens.immediateLogin')}
                                        </>
                                    )}
                                    {userExists && (
                                        <>
                                            <br />{t('hardcoded.components_auth_createusermanually.usuario_existente_se_agregara_a')}</>
                                    )}
                                </Typography>
                            </Alert>
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckIcon />
                                <Typography variant="body2">
                                    <strong>{t('createUser.notifications.userCreated')}!</strong>
                                    {createdUser?.welcome_email_sent ? ` ${t('createUser.success.emailSentNote')}` : ''}
                                </Typography>
                            </Box>
                        </Alert>

                        <Box sx={{ backgroundColor: 'grey.50', p: 3, borderRadius: 2, mb: 3 }}>
                            <Typography variant="h6" color="primary" gutterBottom>
                                👤 {t('createUser.success.userInfo')}
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('createUser.success.fullName')}:
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {createdUser?.user?.full_name || createdUser?.full_name}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">Email:</Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {createdUser?.user?.email || createdUser?.email}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('users.table.role')}:
                                    </Typography>
                                    <Chip
                                        label={roleOptions.find(r => r.value === (createdUser?.user?.type || createdUser?.type))?.label || t('inviteUser.roles.user.label')}
                                        color={roleOptions.find(r => r.value === (createdUser?.user?.type || createdUser?.type))?.color || 'default'}
                                        size="small"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('audit.detail.emailSent')}:
                                    </Typography>
                                    <Chip
                                        label={createdUser?.welcome_email_sent ? t('common.yes') : t('common.no')}
                                        color={createdUser?.welcome_email_sent ? 'success' : 'warning'}
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Alert severity={createdUser?.welcome_email_sent ? 'success' : 'warning'}>
                            <Typography variant="body2">
                                {createdUser?.welcome_email_sent ? (
                                    <>
                                        <strong>✅ {t('createUser.success.emailSentNote')}</strong>
                                    </>
                                ) : (
                                    <>
                                        <strong>⚠️ {t('createUser.success.emailNotSentNote')}</strong>
                                    </>
                                )}
                            </Typography>
                        </Alert>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={handleClose}
                    color="inherit"
                >
                    {step === 'create' ? t('common.cancel') : t('common.close')}
                </Button>
                {step === 'create' && (
                    <Button
                        onClick={formik.handleSubmit}
                        variant="contained"
                        disabled={loading || !formik.isValid || checkingEmail}
                        sx={{ minWidth: 140 }}
                        startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                    >
                        {loading ? t('createUser.form.creating') : t('createUser.form.createUser')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateUserManually;